import React, { useEffect, useRef, useState } from "react";
import { useMyContext } from '../context/Context';
import { getItemDetails, searchItems, searchItemsForVendors } from "../services/apiService";
import { disableBack4Week, disableNext4Week, getCurrentFirstDate } from "../services/calendarService";
import { downloadHistoricInventoryData } from '../services/historicDownloadService';
import moment from 'moment';
import { AppBar, List, ListItem, ListItemText, Paper, TextField } from "@mui/material";
import styles from "./../styles/HeaderFooterStyles.module.scss";

import { Helmet } from "react-helmet";
import { ReactComponent as Bookmark } from "../assets/header_icons/Bookmark.svg";
import { ReactComponent as Chevron } from "../assets/header_icons/Chevron.svg";
import { ReactComponent as Download } from "../assets/header_icons/Download.svg";
import { ReactComponent as Settings } from "../assets/header_icons/Settings.svg";
import { ReactComponent as History } from "../assets/header_icons/History.svg";
import { ReactComponent as ListIcon } from "../assets/header_icons/List.svg";
import { ReactComponent as LightThemeIcon } from '../assets/header_icons/LightTheme.svg';
import { ReactComponent as DarkThemeIcon } from '../assets/header_icons/DarkTheme.svg';
import { ReactComponent as Pencil } from "../assets/Pencil-icon.svg";
import { NavigationButton } from "../components/common/NavigationButton";
import DatePicker from "../components/datePicker";
import { DownloadAlert } from "../components/common/alert";
import DownloadDialog from "../components/download/DownloadDialog";
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import Item from '../models/Item';
import { ENV } from "../constants";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import KeyboardShortcutsBar from '../components/KeyboardShortcutsBar';

const StyledMenu = props => {
  const { colorTheme } = useMyContext();
  const isDark = colorTheme === 'dark';

  return (
    <Menu
      {...props}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '8px',
          minWidth: 250,
          boxShadow: '0px 8px 24px rgba(0, 39, 81, 0.15)',
          marginTop: '8px',
          fontFamily: 'inherit',
          padding: 0,
          border: isDark ? '1px solid #2A2A2A' : '1px solid #e5e8ee',
          background: isDark ? '#1A1A1A' : '#fff'
        },
        '& .MuiMenuItem-root': {
          color: isDark ? '#0071E9' : '#0060C6',
          fontSize: 16,
          fontWeight: 'bold',
          padding: '12px 24px',
          borderBottom: isDark ? '1px solid #2A2A2A' : '1px solid #e5e8ee',
          background: isDark ? '#1A1A1A' : '#fff',
          cursor: 'pointer',
          transition: 'background 0.2s',
          '&:last-child': {
            borderBottom: 'none'
          },
          '&:hover': {
            background: isDark ? '#2A2A2A' : '#f2f4f6',
            color: isDark ? '#0071E9' : '#0060C6'
          },
          '&:active': {
            background: isDark ? '#3A3A3A' : '#e5e8ee',
            color: isDark ? '#0071E9' : '#0060C6'
          }
        }
      }}
    />
  );
};

const StyledPopOver = styled(Popover)(({ theme }) => ({
  '& .MuiPaper-root': {
    width: '336px',
    maxWidth: '336px',
    borderRadius: '0 !important',
    boxShadow: '0px 8px 24px rgba(0, 39, 81, 0.15)',
    marginTop: '4px'
  }
}));

// Pure formatting helper, extracted so it can be unit-tested in isolation
// without mounting the full Header component.
export function formatItemCountLabel(count) {
  return `${count} item${count === 1 ? '' : 's'}`;
}

const Header = ({ showHideSidenav, showHideBookmarkDrawer }) => {
  const context = useMyContext();
  const colorTheme = context.colorTheme || 'light';
  const setColorTheme = context.setColorTheme || (() => {});
  const isDark = colorTheme === 'dark';
  // Prefer the explicit isWpidSearch flag set at search time — falls back to
  // the data-shape check only when the flag isn't present (e.g. existing
  // test mocks that construct context manually), so an empty WPID result
  // set or a result missing wpidNbr can't silently defeat mode detection.
  const isNonVariantMode = context.nonVariantEnabled && (context.isWpidSearch ?? !!context.selectedItem?.wpidNbr);

  const [openDropdown, setOpenDropdown] = useState(false);
  const [currentSelectedItem, setCurrentSelectedItem] = React.useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isDownloading, setDownloading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [vendorItems, setVendorItems] = useState([]);
  const [currentVendorItemIndex, setCurrentVendorItemIndex] = useState(0);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [exportType, setExportType] = useState("standard");
  const [itemSearchInput, setItemSearchInput] = useState('');
  const [isItemSearchActive, setIsItemSearchActive] = useState(false);
  const [isDCSelectOpen, setIsDCSelectOpen] = useState(false);
  const [dcSearchInput, setDCSearchInput] = useState('');
  const [filteredDCs, setFilteredDCs] = useState([]);
  const [highlightedDCIndex, setHighlightedDCIndex] = useState(-1);
  const [justClosedDCSelect, setJustClosedDCSelect] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [wpidItemsPopoverAnchor, setWpidItemsPopoverAnchor] = useState(null);
  const dcInputRef = useRef(null);
  const itemInputRef = useRef(null);
  const highlightedDCRef = useRef(null);

  useEffect(() => {
    if (highlightedDCRef.current) {
      highlightedDCRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [highlightedDCIndex]);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isHistoricView = context.switchSearch === 'compare';
  const open = !isHistoricView && Boolean(anchorEl);
  const popOverId = open ? 'simple-popover' : undefined;

  const mainTitle = ENV === "prod" ?  "Home | DDIR" : `Home | DDIR (${ENV})`;

  const emptyItemDetails = new Item(
    {
      "itemNbr": context.selectedItem ? context.selectedItem.itemNbr : 'not available',
      "itemDesc": context.selectedItem ? context.selectedItem.itemDesc : 'not available',
      "upcNbr": context.selectedItem ? context.selectedItem.upcNbr : 0,
      "ossOnHandCaseQty": "N/A",
      "vendorName": context.selectedItem ? context.selectedItem.vendorName : 'not available',
      "category": context.selectedItem ? context.selectedItem.category : 'N/A'
    });

  const pfKeyActions = {
    PF1: () => {
      if (context.switchSearch === 'compare') {
        context.showMsgCallback('Date navigation is disabled in historic comparison view.');
        return;
      }

      // Show 1 week before current + current week + 2 ahead, firstDate = WM week start - 1 week.
      // Must use getCurrentFirstDate() (Saturday-based WM week) rather than moment's startOf('week')
      // (which anchors to Sunday), as a Sunday anchor shifts endDate by 1 day into the next WM week.
      const targetFirstDate = getCurrentFirstDate();

      if (context.firstDate.isSame(targetFirstDate, 'week')) {
        context.showMsgCallback('Already viewing the current 4 weeks.');
        return;
      }

      context.firstDateChangeCallback(targetFirstDate);
    },
    PF2: () => {
      if (context.switchSearch === 'compare') {
        context.showMsgCallback('Date navigation is disabled in historic comparison view.');
        return;
      }

      const isDisabled = disableNext4Week(context.firstDate, context.calEndDate);
      if (isDisabled) {
        context.showMsgCallback('Cannot move forward 4 weeks - you have reached the end of the available date range.');
        return;
      }

      const newDate = context.firstDate.clone().add(4, 'weeks');
      context.firstDateChangeCallback(newDate);
    },
    PF3: () => {
      if (context.switchSearch === 'compare') {
        context.showMsgCallback('Date navigation is disabled in historic comparison view.');
        return;
      }

      const isDisabled = disableBack4Week(context.firstDate, context.calStartDate);
      if (isDisabled) {
        context.showMsgCallback('Cannot move backward 4 weeks - you have reached the beginning of the available date range.');
        return;
      }

      const newDate = context.firstDate.clone().subtract(4, 'weeks');
      context.firstDateChangeCallback(newDate);
    },
    PF4: () => {
      searchLeftHandler();
    },
    PF5: () => {
      searchRightHandler();
    },
    PF7: () => {
      if (isNonVariantMode) return;
      if (vendorItems.length === 0) {
        context.showMsgCallback('No vendor items available for navigation.');
        return;
      }

      if (vendorItems.length === 1) {
        context.showMsgCallback('Only 1 item available for this vendor.');
        return;
      }

      if (currentVendorItemIndex > 0) {
        const prevItem = vendorItems[currentVendorItemIndex - 1];
        context.selectItemCallback(prevItem);
        setCurrentVendorItemIndex(currentVendorItemIndex - 1);
      } else {
        const lastItem = vendorItems[vendorItems.length - 1];
        context.selectItemCallback(lastItem);
        setCurrentVendorItemIndex(vendorItems.length - 1);
      }
    },
    PF8: () => {
      if (isNonVariantMode) return;
      if (vendorItems.length === 0) {
        context.showMsgCallback('No vendor items available for navigation.');
        return;
      }

      if (vendorItems.length === 1) {
        context.showMsgCallback('Only 1 item available for this vendor.');
        return;
      }

      if (currentVendorItemIndex < vendorItems.length - 1) {
        const nextItem = vendorItems[currentVendorItemIndex + 1];
        context.selectItemCallback(nextItem);
        setCurrentVendorItemIndex(currentVendorItemIndex + 1);
      } else {
        const firstItem = vendorItems[0];
        context.selectItemCallback(firstItem);
        setCurrentVendorItemIndex(0);
      }
    }
  };

  // Get Item Details
  useEffect(() => {
    if (context.selectedItem && context.searchedDCNbr && !isSearching) {
      if (isNonVariantMode) {
        // WPID mode: item info is already present in the search result — skip /fetchItem
        setCurrentSelectedItem(context.selectedItem);
        context.setItemAvailable(true);
      } else {
        setIsItemSearchActive(true);
        setItemSearchInput(context.selectedItem.itemNbr.toString());
        getItemDetails(
          context.selectedItem.itemNbr,
          context.selectedItem.legacyItemNbr,
          context.searchedDCNbr,
        ).then(result => {
          setCurrentSelectedItem(result);
          context.setItemAvailable(true);
          fetchVendorItems(result);
        }).catch(err => {
          setCurrentSelectedItem(emptyItemDetails);
          context.setItemAvailable(false);
        });
      }
    } else {
      setCurrentSelectedItem(null);
    }
  }, [context.selectedItem, context.searchedDCNbr, isSearching]);

  const fetchVendorItems = async itemDetails => {
    try {
      const vendorId = itemDetails?.vendorNbr;

      if (!vendorId || !context.searchedDCNbr) {
        setVendorItems([]);
        setCurrentVendorItemIndex(0);
        return;
      }

      const vendorSearchResult = await searchItemsForVendors([vendorId], context.searchedDCNbr);
      if (!vendorSearchResult.itemInfo || vendorSearchResult.itemInfo.length === 0) {
        setVendorItems([]);
        setCurrentVendorItemIndex(0);
        context.showMsgCallback('No vendor items found for this vendor.');
        return;
      }

      setVendorItems(vendorSearchResult.itemInfo);

      const currentIndex = vendorSearchResult.itemInfo.findIndex(
        item => item.itemNbr === context.selectedItem.itemNbr
      );
      setCurrentVendorItemIndex(Math.max(0, currentIndex));

    } catch (error) {
      console.error('Error fetching vendor items:', error);
      setVendorItems([]);
      setCurrentVendorItemIndex(0);
      context.showMsgCallback('Failed to load vendor items. Please try again.');
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', detectKeyDown);

    return () => {
      document.removeEventListener("keydown", detectKeyDown);
    };
  }, [context.selectedItem, context.switchSearch, context.itemAvailable, pfKeyActions]);

  useEffect(() => {
    if (isDCSelectOpen && dcInputRef.current) {
      setTimeout(() => {
        dcInputRef.current?.focus();
      }, 0);
    }
  }, [isDCSelectOpen]);

  useEffect(() => {
    if (isItemSearchActive && itemInputRef.current) {
      setTimeout(() => {
        itemInputRef.current?.focus();
        itemInputRef.current?.select();
      }, 0);
    }
  }, [isItemSearchActive]);

  const detectKeyDown = e => {
    const inputTypes = ['input', 'textarea'];
    const isInInputField = document.activeElement && inputTypes.includes(document.activeElement.tagName.toLowerCase());
    const isInDCInput = isDCSelectOpen && dcInputRef.current && document.activeElement === dcInputRef.current;

    // Allow F-keys to work even in input fields
    const isFunctionKey = e.key.startsWith('F') && e.key.length <= 3;

    if (isInInputField && !isFunctionKey && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
      return;
    }

    if (isInDCInput && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      return;
    }

    if (e.key === 'Tab' && context.selectedItem && !isNonVariantMode) {
      e.preventDefault();
      e.stopPropagation();

      if (!isDCSelectOpen) {
        if (!isItemSearchActive) {
          setIsItemSearchActive(true);
          setItemSearchInput(currentSelectedItem?.itemNbr?.toString() || '');
        }
        setTimeout(() => {
          if (itemInputRef.current) {
            itemInputRef.current.focus();
            itemInputRef.current.select();
          }
        }, 10);
      }
      return;
    }

    if (e.key.startsWith('F') && e.key.length <= 3) {
      e.preventDefault();
      const keyNumber = parseInt(e.key.substring(1));
      const pfKey = `PF${keyNumber}`;

      if (!pfKeyActions[pfKey]) return;

      const hasValidContext = context.selectedItem && context.searchResult && context.searchResult.length > 0;

      if (hasValidContext) {
        pfKeyActions[pfKey]();
      } else {
        if (context.showMsgCallback) {
          context.showMsgCallback('Please select an item.', 'info');
        } else {
          console.log(`${pfKey} pressed but no item selected or search results available`);
        }
      }
      return;
    }

    if (e.key === "ArrowUp" && context.selectedItem) {
      e.preventDefault();
      searchUpHandler();
    }
    if (e.key === "ArrowDown" && context.selectedItem) {
      e.preventDefault();
      searchDownHandler();
    }
    if (e.shiftKey && e.key === "?" && context.selectedItem) {
      e.preventDefault();
      handleView();
    }
    if (e.key === "B" || e.key === "b") {
      e.preventDefault();
      showHideBookmarkDrawer();
    }
    if (e.key === "S" || e.key === "s") {
      e.preventDefault();
      showHideSidenav();
    }
    if (e.key === "D" || e.key === "d") {
      e.preventDefault();
      onClickDownload();
    }
  };

  const isVendorMode = !isNonVariantMode && !context.showItemSearch;

  const searchRightHandler = () => {
    if (isNonVariantMode) {
      const wpids = [...new Set(context.searchResult.map(r => r.wpidNbr))];
      const currentWpidIndex = wpids.indexOf(context.selectedItem?.wpidNbr);
      const nextWpidNbr = wpids[(currentWpidIndex + 1) % wpids.length];
      const nextItem = context.searchResult.find(r => r.wpidNbr === nextWpidNbr);
      if (nextItem) context.selectItemCallback(nextItem);
    } else if (isVendorMode) {
      const vendors = context.searchedVendorNbrList ?? [];
      const currentVendorKey = context.selectedItem?._searchedVendorNbr ?? context.selectedItem?.vendorNbr;
      const currentVendorIndex = vendors.indexOf(currentVendorKey);
      const nextVendorNbr = vendors[(currentVendorIndex + 1) % vendors.length];
      const nextItem = context.searchResult.find(r => (r._searchedVendorNbr ?? r.vendorNbr) === nextVendorNbr);
      if (nextItem) context.selectItemCallback(nextItem);
    } else {
      let currentIndex = context.getSelectedItemIndexCallback();
      if (currentIndex < context.searchResult.length - 1) {
        context.selectItemCallback(context.searchResult[currentIndex + 1]);
      } else {
        context.selectItemCallback(context.searchResult[0]);
      }
    }
  };

  const searchLeftHandler = () => {
    if (isNonVariantMode) {
      const wpids = [...new Set(context.searchResult.map(r => r.wpidNbr))];
      const currentWpidIndex = wpids.indexOf(context.selectedItem?.wpidNbr);
      const prevWpidNbr = wpids[(currentWpidIndex - 1 + wpids.length) % wpids.length];
      const prevItem = context.searchResult.find(r => r.wpidNbr === prevWpidNbr);
      if (prevItem) context.selectItemCallback(prevItem);
    } else if (isVendorMode) {
      const vendors = context.searchedVendorNbrList ?? [];
      const currentVendorKey = context.selectedItem?._searchedVendorNbr ?? context.selectedItem?.vendorNbr;
      const currentVendorIndex = vendors.indexOf(currentVendorKey);
      const prevVendorNbr = vendors[(currentVendorIndex - 1 + vendors.length) % vendors.length];
      const prevItem = context.searchResult.find(r => (r._searchedVendorNbr ?? r.vendorNbr) === prevVendorNbr);
      if (prevItem) context.selectItemCallback(prevItem);
    } else {
      let currentIndex = context.getSelectedItemIndexCallback();
      if (currentIndex > 0) {
        context.selectItemCallback(context.searchResult[currentIndex - 1]);
      } else {
        context.selectItemCallback(context.searchResult[context.searchResult.length - 1]);
      }
    }
  };

  const searchUpHandler = () => {
    if (context.searchedDcNbrList.length > 1) {
      let currentDcIndex = context.searchedDcNbrList.indexOf(context.searchedDCNbr);
      const currentItemIndex = context.searchResult.findIndex(item => item.itemNbr === context.selectedItem.itemNbr);
      let nextDC = "";
      if (currentDcIndex < context.searchedDcNbrList.length - 1) {
        nextDC = context.searchedDcNbrList[currentDcIndex + 1];
      } else {
        nextDC = context.searchedDcNbrList[0];
      }
      if (isNonVariantMode) {
        context.wpidSearchDcNavCallback(nextDC, currentItemIndex);
      } else if (context.showItemSearch) {
        context.itemSearchDcNavCallback(nextDC, currentItemIndex);
      } else {
        context.vendorSearchDcNavCallback(nextDC, currentItemIndex);
      }
    }
  };

  const searchDownHandler = () => {
    if (context.searchedDcNbrList.length > 1) {
      const currentDcIndex = context.searchedDcNbrList.indexOf(context.searchedDCNbr);
      const currentItemIndex = context.searchResult.findIndex(item => item.itemNbr === context.selectedItem.itemNbr);
      let prevDC = "";
      if (currentDcIndex > 0) {
        prevDC = context.searchedDcNbrList[currentDcIndex - 1];
      } else {
        prevDC = context.searchedDcNbrList[context.searchedDcNbrList.length - 1];
      }
      if (isNonVariantMode) {
        context.wpidSearchDcNavCallback(prevDC, currentItemIndex);
      } else if (context.showItemSearch) {
        context.itemSearchDcNavCallback(prevDC, currentItemIndex);
      } else {
        context.vendorSearchDcNavCallback(prevDC, currentItemIndex);
      }
    }
  };

  const handleDownloading = isDownloading => {
    if (isDownloading) {
      setDownloading(true);
      setShowDialog(false);
      setShowAlert(true);
    } else {
      setDownloading(false);
      setShowAlert(false);
    }
  };

  const onClickDownload = () => {
    if (context.searchResult && context.searchResult.length) {
      setShowDialog(true);
      setOpenDropdown(false);
    }
  };

  const handleDialogClose = () => {
    setShowDialog(false);
  };
  const handleView = () => {
    if (context.switchSearch === 'week' && context.itemAvailable) {
      context.setSwitchSearch('compare');
      context.setShowDurationSelector(false);
      setAnchorEl(null);
    } else if (context.switchSearch === 'compare') {
      context.setSwitchSearch('week');
      context.setShowDurationSelector(true);
    }
  };

  const handleDownloadMenuOpen = event => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleStandardExport = () => {
    setDownloadMenuAnchor(null);
    setShowDialog(true);
    setExportType("standard");
  };

  const handleMeatBuyingExport = () => {
    setDownloadMenuAnchor(null);
    setShowDialog(true);
    setExportType("meat");
  };

  const handleHistoricExport = async () => {
    if (!context.selectedItem) {
      console.warn('No selectedItem found for historic download');
      context.showMsgCallback('No item selected');
      return;
    }
    setDownloadMenuAnchor(null);
    setDownloading(true);
    setShowAlert(true);
    try {
      await downloadHistoricInventoryData(
        context.selectedItem,
        context.searchedDCNbr
      );
    } catch (err) {
      console.error('Historic download failed', err);
      context.showMsgCallback('Historic download failed');
    } finally {
      setDownloading(false);
      setShowAlert(false);
    }
  };

  const handleDownloadClick = e => {
    if (isHistoricView) {
      handleHistoricExport();
    } else {
      handleDownloadMenuOpen(e);
    }
  };

  const handleItemSearch = event => {
    setItemSearchInput(event.target.value);
  };

  useEffect(() => {
    if (!currentSelectedItem) {
      context.setHasUnsavedChanges && context.setHasUnsavedChanges(false);
      return;
    }

    const itemChanged = itemSearchInput &&
                       itemSearchInput.trim() !== '' &&
                       itemSearchInput !== currentSelectedItem.itemNbr.toString();
    const dcChanged = dcSearchInput &&
                     dcSearchInput.trim() !== '' &&
                     parseInt(dcSearchInput.trim()) !== context.searchedDCNbr;

    const hasChanges = itemChanged || dcChanged;
    context.setHasUnsavedChanges && context.setHasUnsavedChanges(hasChanges);
  }, [itemSearchInput, dcSearchInput, currentSelectedItem, context.searchedDCNbr]);

  const handleItemSearchKeyDown = event => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setIsItemSearchActive(false);
      setIsDCSelectOpen(true);
      setFilteredDCs([]);
      setDCSearchInput('');
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      
      if (!itemSearchInput || itemSearchInput.trim() === '') {
        context.showMsgCallback('Please enter an item number');
        return;
      }

      const itemNbr = itemSearchInput.trim();
      const dcNbr = dcSearchInput && dcSearchInput.trim()
        ? parseInt(dcSearchInput.trim())
        : (context.searchedDCNbr || context.searchedDcNbrList[0]);
      
      if (!dcNbr) {
        context.showMsgCallback('Please select a DC first');
        return;
      }

      setIsSearching(true);
      searchItems([itemNbr], dcNbr)
        .then(searchResults => {
          if (searchResults && searchResults.length > 0) {
            context.searchCallback(
              true,
              [itemNbr],
              [],
              dcNbr,
              searchResults,
              [dcNbr]
            );
            context.selectItemCallback(searchResults[0]);
            setIsItemSearchActive(false);
            setDCSearchInput('');
            context.setHasUnsavedChanges && context.setHasUnsavedChanges(false);
          } else {
            context.showMsgCallback(`Item ${itemNbr} not found or not authorized for DC ${dcNbr}`);
          }
        })
        .catch(err => {
          console.error('Search error:', err);
          context.showMsgCallback('Failed to search for item. Please try again.');
        })
        .finally(() => {
          setIsSearching(false);
        });
    }
  };

  const handleItemSearchBlur = () => {
    setTimeout(() => {
      if (!itemSearchInput || itemSearchInput.trim() === '') {
        setIsItemSearchActive(false);
        setItemSearchInput('');
      }
    }, 200);
  };

  const toggleItemSearch = () => {
    if (!isItemSearchActive) {
      setItemSearchInput(currentSelectedItem?.itemNbr?.toString() || '');
    } else {
      setItemSearchInput('');
    }
    setIsItemSearchActive(!isItemSearchActive);
  };

  const handleDCSearchChange = event => {
    const value = event.target.value;
    setDCSearchInput(value);
    setHighlightedDCIndex(-1);
    
    if (value && value.length > 0) {
      const filtered = (context.dcNbrList || []).filter(dc => {
        const paddedDC = dc.toString().length === 4 ? '0' + dc : dc.toString();
        return paddedDC.includes(value);
      });
      setFilteredDCs(filtered);
    } else {
      setFilteredDCs([]);
    }
  };

  const handleDCSelect = dcNum => {
    const currentItemIndex = context.searchResult.findIndex(
      item => item.itemNbr === context.selectedItem.itemNbr
    );

    if (isNonVariantMode) {
      context.wpidSearchDcNavCallback(dcNum, currentItemIndex);
    } else if (context.showItemSearch) {
      context.itemSearchDcNavCallback(dcNum, currentItemIndex);
    } else {
      context.vendorSearchDcNavCallback(dcNum, currentItemIndex);
    }
    
    setIsDCSelectOpen(false);
    setDCSearchInput('');
    setFilteredDCs([]);
    setJustClosedDCSelect(true);
    
    setTimeout(() => {
      setJustClosedDCSelect(false);
    }, 100);
  };

  const handleDCSearchBlur = () => {
    const inputValue = dcInputRef.current?.value || '';
    setTimeout(() => {
      if (inputValue && inputValue.trim()) {
        const dcNum = parseInt(inputValue.trim());
        if (isNaN(dcNum)) {
          setIsDCSelectOpen(false);
          setDCSearchInput('');
          setFilteredDCs([]);
          return;
        }
        if (context.dcNbrList) {
          const dcExists = context.dcNbrList.some(dc => {
            const dcInt = typeof dc === 'string' ? parseInt(dc) : dc;
            return dcInt === dcNum;
          });
          if (dcExists) {
            handleDCSelect(dcNum);
            return;
          }
        }
      }
      setIsDCSelectOpen(false);
      setDCSearchInput('');
      setFilteredDCs([]);
    }, 200);
  };

  const toggleDCSelect = () => {
    if (!isDCSelectOpen) {
      setFilteredDCs(context.dcNbrList || []);
    }
    setIsDCSelectOpen(!isDCSelectOpen);
    setDCSearchInput('');
  };

  const handleDCSearchKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (filteredDCs.length === 0) {
        setFilteredDCs(context.dcNbrList || []);
        setHighlightedDCIndex(0);
      } else {
        const newIndex = highlightedDCIndex < filteredDCs.length - 1 ? highlightedDCIndex + 1 : highlightedDCIndex;
        setHighlightedDCIndex(newIndex);
      }
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (filteredDCs.length > 0) {
        const newIndex = highlightedDCIndex > 0 ? highlightedDCIndex - 1 : 0;
        setHighlightedDCIndex(newIndex);
      }
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      setIsDCSelectOpen(false);
      setFilteredDCs([]);
      setIsItemSearchActive(true);
      if (!itemSearchInput) {
        setItemSearchInput(currentSelectedItem?.itemNbr?.toString() || '');
      }
      setJustClosedDCSelect(true);
      setTimeout(() => {
        setJustClosedDCSelect(false);
      }, 100);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (highlightedDCIndex >= 0 && filteredDCs.length > 0) {
        handleDCSelect(filteredDCs[highlightedDCIndex]);
        return;
      }

      if (itemSearchInput && itemSearchInput.trim() !== currentSelectedItem?.itemNbr?.toString()) {
        const itemNbr = itemSearchInput.trim();
        const dcNum = filteredDCs.length > 0
          ? filteredDCs[0]
          : (dcSearchInput.trim() ? parseInt(dcSearchInput.trim()) : null);

        if (!dcNum || isNaN(dcNum)) {
          context.showMsgCallback('Please enter a DC number');
          return;
        }

        const dcExists = context.dcNbrList && context.dcNbrList.some(dc => {
          const dcInt = typeof dc === 'string' ? parseInt(dc) : dc;
          return dcInt === dcNum;
        });

        if (!dcExists) {
          context.showMsgCallback('Invalid DC number. Please enter a valid DC.');
          return;
        }

        setIsSearching(true);
        searchItems([itemNbr], dcNum)
          .then(searchResults => {
            if (searchResults && searchResults.length > 0) {
              context.searchCallback(
                true,
                [itemNbr],
                [],
                dcNum,
                searchResults,
                [dcNum]
              );
              context.selectItemCallback(searchResults[0]);
              setIsItemSearchActive(false);
              setIsDCSelectOpen(false);
              setDCSearchInput('');
              setFilteredDCs([]);
              context.setHasUnsavedChanges && context.setHasUnsavedChanges(false);
            } else {
              context.showMsgCallback(`Item ${itemNbr} not found or not authorized for DC ${dcNum}`);
            }
          })
          .catch(err => {
            console.error('Search error:', err);
            context.showMsgCallback('Failed to search for item. Please try again.');
          })
          .finally(() => {
            setIsSearching(false);
          });
        return;
      }

      if (filteredDCs.length > 0) {
        handleDCSelect(filteredDCs[0]);
      } else if (dcSearchInput.trim()) {
        const dcNum = parseInt(dcSearchInput.trim());
        
        if (isNaN(dcNum)) {
          context.showMsgCallback('Please enter a valid DC number.');
          return;
        }

        // Check if DC exists (handle both with and without leading zeros)
        const dcExists = context.dcNbrList && context.dcNbrList.some(dc => {
          const dcInt = typeof dc === 'string' ? parseInt(dc) : dc;
          return dcInt === dcNum;
        });

        if (dcExists) {
          handleDCSelect(dcNum);
        } else {
          context.showMsgCallback('Invalid DC number. Please enter a valid DC.');
        }
      }
    } else if (event.key === 'Escape') {
      setIsDCSelectOpen(false);
      setDCSearchInput('');
      setFilteredDCs([]);
    }
  };

  useEffect(() => {
    const lightCls = 'theme-light';
    const darkCls = 'theme-dark';
    const body = document.body;

    body.classList.remove(lightCls, darkCls);
    if (colorTheme === 'dark') {
      body.classList.add(darkCls);
    } else {
      body.classList.add(lightCls);
    }
  }, [colorTheme]);

  // Render Item Info State
  if (currentSelectedItem && context.searchedDCNbr) {
    return (
      <AppBar
        position="static"
        color="inherit"
        className={[
          styles.header,
          colorTheme === 'dark' ? styles.darkTheme : styles.lightTheme
        ].join(' ')}
      >
        <Helmet>
          <title>{mainTitle}</title>
        </Helmet>
        <div className={styles.itemDisplay}>
          <div className={styles.itemContent}>
            <img
              data-testid="header-img"
              src={
                currentSelectedItem && currentSelectedItem.itemImgUrl
                  ? currentSelectedItem.itemImgUrl
                  : `https://scene7.samsclub.com/is/image/samsclub/0040640600219`
              }
              className={styles.itemImage}
              alt="current item"
            />
            <div className={styles.itemInfoContainer}>
              <div className={styles.itemNumber}>
                {isNonVariantMode ? (
                  <>
                    <span>WPID:</span>
                    <span>{context.selectedItem?.wpidNbr}</span>
                  </>
                ) : (
                  <>
                    <span>Item:</span>
                    {isItemSearchActive ? (
                      <TextField
                        inputRef={itemInputRef}
                        autoFocus
                        variant="standard"
                        size="small"
                        value={itemSearchInput}
                        onChange={handleItemSearch}
                        onKeyDown={handleItemSearchKeyDown}
                        onBlur={handleItemSearchBlur}
                        onFocus={e => {
                          e.target.select();
                        }}
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            fontSize: '32px',
                            fontWeight: '500',
                            color: isDark ? '#E6E6E6' : '#00172F',
                            lineHeight: '32px',
                            height: '32px',
                            padding: 0,
                            margin: 0,
                            minWidth: 0,
                            borderBottom: isDark ? '2px solid #0071E9' : '2px solid #A2B3BE'
                          }
                        }}
                        inputProps={{
                          style: {
                            padding: 0,
                            margin: 0,
                            height: '32px',
                            lineHeight: '32px',
                            boxSizing: 'border-box',
                            width: `${Math.max(itemSearchInput.length * 19, 100)}px`
                          }
                        }}
                        sx={{
                          width: 'auto',
                          minWidth: 0,
                          margin: 0,
                          padding: 0,
                          '& .MuiInput-root': {
                            height: '32px',
                            margin: 0,
                            padding: 0,
                            minWidth: 0,
                            width: 'auto',
                            '&:before, &:after': {
                              display: 'none'
                            }
                          }
                        }}
                      />
                    ) : (
                      <span>
                        {itemSearchInput && itemSearchInput !== currentSelectedItem.itemNbr.toString()
                          ? itemSearchInput
                          : currentSelectedItem.itemNbr}
                      </span>
                    )}
                    <Pencil
                      onClick={toggleItemSearch}
                      style={{
                        cursor: 'pointer',
                        width: '16px',
                        height: '16px',
                        filter: isDark ? 'brightness(0) invert(0.7)' : 'none'
                      }}
                    />
                  </>
                )}
              </div>
              {isNonVariantMode ? (
                <>
                  <span className={styles.itemDescription} id="current-item-description">
                    Category: {currentSelectedItem.category}
                  </span>
                  <span className={styles.itemDetail}>
                    {(() => {
                      const wpidItems = context.searchResult?.filter(
                        r => r.wpidNbr === context.selectedItem?.wpidNbr
                      ) || [];

                      const itemLink = r => (
                        <a
                          href={`/dashboard?item=${r.itemNbr}&dc=${context.searchedDCNbr}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: isDark ? '#4DA3FF' : '#0060C6', textDecoration: 'underline', fontFamily: 'inherit', fontSize: 'inherit' }}
                        >{r.itemNbr}</a>
                      );

                      const popover = (anchor, items) => (
                        <Popover
                          open={Boolean(anchor)}
                          anchorEl={anchor}
                          onClose={() => setWpidItemsPopoverAnchor(null)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          PaperProps={{ sx: { maxHeight: 300, overflowY: 'auto', padding: '8px 16px', minWidth: 160, borderRadius: '8px', boxShadow: '0px 8px 24px rgba(0,39,81,0.15)', background: isDark ? '#1A1A1A' : '#fff', border: isDark ? '1px solid #2A2A2A' : '1px solid #e5e8ee' } }}
                        >
                          {items.map(r => (
                            <div key={r.itemNbr} style={{ padding: '4px 0', fontSize: '13px', fontFamily: 'inherit' }}>
                              {itemLink(r)}
                            </div>
                          ))}
                        </Popover>
                      );

                      return (
                        <>
                          Items:{' '}
                          <button
                            onClick={e => setWpidItemsPopoverAnchor(e.currentTarget)}
                            style={{ background: 'none', border: `1px solid ${isDark ? '#4DA3FF' : '#0060C6'}`, borderRadius: 12, padding: '1px 10px', cursor: 'pointer', color: isDark ? '#4DA3FF' : '#0060C6', fontSize: '12px', fontFamily: 'inherit' }}
                          >
                            {formatItemCountLabel(wpidItems.length)} ▾
                          </button>
                          {popover(wpidItemsPopoverAnchor, wpidItems)}
                        </>
                      );
                    })()}
                  </span>
                </>
              ) : (
                <>
                  <span className={styles.itemDescription} id="current-item-description">
                    {currentSelectedItem.itemDesc} --&nbsp;
                    <span className={currentSelectedItem.ossOnHandCaseQty > 0 && styles.outStorageText}>
                      Out Storage: {Object.prototype.hasOwnProperty.call(currentSelectedItem, 'ossOnHandCaseQty')
                        ? currentSelectedItem.ossOnHandCaseQty.toLocaleString()
                        : '--'}
                    </span>
                  </span>
                  <span className={styles.itemDetail}>
                    Category : {currentSelectedItem.category} {'\u00A0'} Vendor : {currentSelectedItem.vendorNbr} - {currentSelectedItem.vendorName}
                  </span>
                </>
              )}
            </div>
          </div>
          {/* Item Nav */}
          <div className={styles.itemNav} id="item-navigation">
            <NavigationButton
              title={isNonVariantMode ? "Previous WPID (Keyboard: F4)" : "Previous item (Keyboard: F4)"}
              id="prev-item"
              tipPosition="right"
              type="nav"
              clickHandler={() => searchLeftHandler()}
            >
              <Chevron className={styles.chevLeft} />
            </NavigationButton>
            <NavigationButton
              title={isNonVariantMode ? "Next WPID (Keyboard: F5)" : "Next item (Keyboard: F5)"}
              id="next-item"
              tipPosition="right"
              type="nav"
              clickHandler={() => searchRightHandler()}
            >
              <Chevron className={styles.chevRight} />
            </NavigationButton>
            {/* DC Nav */}
            <div className={styles.dcNav}>
              <NavigationButton
                title="Previous DC (Keyboard: Arrow down)"
                id="prev-DC"
                tipPosition="right"
                type="nav"
                clickHandler={() => searchDownHandler()}
              >
                <Chevron className={styles.chevDown} />
              </NavigationButton>
              
              <div className={styles.dcSelectorContainer}>
                {isDCSelectOpen ? (
                  <>
                    <TextField
                      inputRef={dcInputRef}
                      autoFocus
                      value={dcSearchInput}
                      onChange={handleDCSearchChange}
                      onKeyDown={handleDCSearchKeyDown}
                      onBlur={handleDCSearchBlur}
                      variant="standard"
                      placeholder="DC #"
                      inputProps={{
                        'aria-expanded': isDCSelectOpen,
                        'aria-controls': 'dc-dropdown-list',
                        'aria-haspopup': 'listbox',
                        'aria-label': 'Search distribution center number',
                        'role': 'combobox'
                      }}
                      InputProps={{
                        disableUnderline: true,
                        style: {
                          fontSize: '16px',
                          fontWeight: '500',
                          color: isDark ? '#E6E6E6' : '#002D5D',
                          paddingLeft: '12px',
                          paddingRight: '12px',
                          lineHeight: '18px',
                          boxSizing: 'border-box',
                          width: '100%'
                        }
                      }}
                      style={{ margin: 0, width: '100%' }}
                    />
                    {filteredDCs.length > 0 && (
                      <Paper
                        id="dc-dropdown-list"
                        role="listbox"
                        aria-label="Distribution center options"
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: '12px',
                          right: '0',
                          zIndex: 1300,
                          backgroundColor: isDark ? '#1A1A1A' : '#fff',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          marginTop: '4px',
                          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)'
                        }}
                      >
                        <List style={{ padding: 0 }}>
                          {filteredDCs.map((dcNum, index) => (
                            <ListItem
                              key={dcNum}
                              button
                              role="option"
                              ref={index === highlightedDCIndex ? highlightedDCRef : null}
                              aria-selected={context.searchedDCNbr === dcNum || index === highlightedDCIndex}
                              onClick={() => handleDCSelect(dcNum)}
                              style={{
                                color: isDark ? '#E6E6E6' : '#00172F',
                                padding: '8px 16px',
                                cursor: 'pointer',
                                backgroundColor: index === highlightedDCIndex
                                  ? (isDark ? '#2A2A2A' : '#f5f5f5')
                                  : 'transparent'
                              }}
                              onMouseEnter={e => {
                                e.currentTarget.style.backgroundColor = isDark ? '#2A2A2A' : '#f5f5f5';
                              }}
                              onMouseLeave={e => {
                                e.currentTarget.style.backgroundColor = index === highlightedDCIndex
                                  ? (isDark ? '#2A2A2A' : '#f5f5f5')
                                  : 'transparent';
                              }}
                            >
                              <ListItemText primary={dcNum.toString().length === 4 ? '0' + dcNum : dcNum} />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                  </>
                ) : (
                  <span 
                    className={styles.dcNumber}
                    id="dc-current-value"
                    aria-live="polite"
                  >
                    DC {dcSearchInput && dcSearchInput.trim() && parseInt(dcSearchInput.trim()) !== context.searchedDCNbr
                      ? dcSearchInput
                      : context.searchedDCNbr}
                  </span>
                )}
              </div>

              <button
                onClick={toggleDCSelect}
                aria-label={isDCSelectOpen ? 'Close DC selector' : 'Open DC selector'}
                aria-expanded={isDCSelectOpen}
                aria-controls="dc-dropdown-list"
                aria-haspopup="listbox"
                style={{
                  cursor: 'pointer',
                  width: '14px',
                  height: '14px',
                  marginLeft: '8px',
                  marginRight: '8px',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Pencil
                  style={{
                    width: '14px',
                    height: '14px',
                    filter: isDark ? 'brightness(0) invert(0.7)' : 'none',
                    pointerEvents: 'none'
                  }}
                />
              </button>
              
              <NavigationButton
                title="Next DC (Keyboard: Arrow up)"
                id="next-DC"
                tipPosition="right"
                type="nav"
                clickHandler={() => searchUpHandler()}
              >
                <Chevron className={styles.chevUp} />
              </NavigationButton>
            </div>
          </div>
        </div>
        {/* theme toggle */}
        <div className={styles.themeToggleWrapper}>
          <div className={styles.themeToggle} role="group" aria-label="Color theme">
            <button
              type="button"
              className={`${styles.themeBtn} ${colorTheme === 'light' ? styles.themeBtnActive : ''}`}
              onClick={() => {
                setColorTheme('light');
              }}
              aria-pressed={colorTheme === 'light'}
              aria-label="Light theme"
            >
              <LightThemeIcon className={styles.themeIcon} />
            </button>
            <button
              type="button"
              className={`${styles.themeBtn} ${colorTheme === 'dark' ? styles.themeBtnActive : ''}`}
              onClick={() => {
                setColorTheme('dark');
              }}
              aria-pressed={colorTheme === 'dark'}
              aria-label="Dark theme"
            >
              <DarkThemeIcon className={styles.themeIcon} />
            </button>
          </div>
        </div>
        <div className={styles.rightToolbar}>
          <div className={styles.historyNav}>
            <NavigationButton
              title={context.switchSearch === 'week' ? "Historic comparison (Keyboard: ‘?’)" : "Toggle view (Keyboard: ‘?’)"}
              id="compare-view"
              tipPosition="right"
              type="nav"
              clickHandler={handleView}
              disabled={!context.itemAvailable && context.switchSearch === 'week'}
            >
              {context.switchSearch === 'week' ? <History className={context.itemAvailable ? "" : styles.iconDisabled} /> : <ListIcon className={context.itemAvailable ? "" : styles.iconDisabled} />}
            </NavigationButton>
          </div>
          {/* calendar box */}
          <Tooltip
            title={isHistoricView
              ? 'Date selection is disabled in historic comparison view. Press ? to return to weekly view.'
              : ''
            }
            placement="bottom-start"
            arrow
          >
            <span className={styles.historyNav}>
              <button
                type="button"
                id="date-component"
                className={
                  styles.calendarPlaceholder + (isHistoricView ? ' ' + styles.calendarDisabled : '')
                }
                aria-describedby={popOverId}
                onClick={isHistoricView ? undefined : handleClick}
                aria-label="Open date picker"
                disabled={isHistoricView}
                aria-disabled={isHistoricView}
              >
                {context.firstDate.format("MMMM YYYY")}
              </button>
            </span>
          </Tooltip>
          <StyledPopOver
            data-testid="pop-over"
            id={popOverId}
            open={!isHistoricView && open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            sx={{
              '& .MuiPaper-root': {
                backgroundColor: isDark ? '#1A1A1A' : '#FFFFFF',
                color: isDark ? '#E6E6E6' : '#00172F'
              }
            }}
          >
            <DatePicker handleClose={handleClose} colorTheme={colorTheme} />
          </StyledPopOver>
          {/* icons */}
          <div className={styles.iconButtonsContainer}>
            <NavigationButton
              data-testid="nav-bookmark-btn"
              clickHandler={showHideBookmarkDrawer}
              title="Bookmarks (Keyboard: 'B')"
              id="save-button"
              tipPosition="left"
              type="icon"
            >
              <Bookmark className={styles.bookmarkIcon} />
            </NavigationButton>
            <NavigationButton
              title="Download set (Keyboard: 'D')"
              id="download-btn"
              tipPosition="left"
              type="icon"
              clickHandler={handleDownloadClick}
              disabled={isDownloading}
            >
              <Download className={styles.kebabIcon} />
            </NavigationButton>
            <StyledMenu
              anchorEl={downloadMenuAnchor}
              open={Boolean(downloadMenuAnchor)}
              onClose={handleDownloadMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
            >
              <MenuItem onClick={handleStandardExport}>Standard Export</MenuItem>
              {!isNonVariantMode && <MenuItem onClick={handleMeatBuyingExport}>Meat Buying Export</MenuItem>}
            </StyledMenu>
            <NavigationButton
              data-testid="nav-search-btn"
              clickHandler={showHideSidenav}
              title="Search and filter items (Keyboard: 'S')"
              id="search-button"
              tipPosition="left"
              type="icon"
            >
              <Settings className={styles.unionIcon} />
            </NavigationButton>
          </div>
        </div>
        <KeyboardShortcutsBar
          colorTheme={colorTheme}
          vendorItemsCount={vendorItems.length}
          currentVendorItemIndex={currentVendorItemIndex}
          isNonVariantMode={isNonVariantMode}
        />
        <DownloadDialog
          data-testid="download-dialog"
          open={showDialog}
          handleClose={handleDialogClose}
          handleDownloading={handleDownloading}
          exportType={exportType}
        />
        <DownloadAlert data-testid="download-alert" open={showAlert} handleClose={() => setShowAlert(false)} />
      </AppBar>
    );
  } else {
    return (
      <AppBar
        position="static"
        color="inherit"
        className={[
          styles.header,
          colorTheme === 'dark' ? styles.darkTheme : styles.lightTheme
        ].join(' ')}
      >
        <Helmet>
          <title>{mainTitle}</title>
        </Helmet>
        <div className={styles.leftSpacer} />
        <div className={styles.rightToolbar}>
          {/* icons */}
          <div className={styles.iconButtonsContainer}>
            <NavigationButton
              clickHandler={showHideBookmarkDrawer}
              title="Bookmarks (Keyboard: 'B')"
              id="save-button"
              tipPosition="left"
              type="icon"
            >
              <Bookmark className={styles.bookmarkIcon} />
            </NavigationButton>
            <NavigationButton
              clickHandler={showHideSidenav}
              title="Search and filter items (Keyboard: 'S')"
              id="search-button"
              tipPosition="left"
              type="icon"
            >
              <Settings className={styles.unionIcon} />
            </NavigationButton>
          </div>
        </div>
      </AppBar>
    );
  }
};

export default Header;